import { useEffect, useState } from "react";
import styles from "./MyAppsDetail.module.css";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Snackbar,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IoArrowBackSharp } from "react-icons/io5";
import { FaExternalLinkAlt } from "react-icons/fa";
import { convertDateFromTFormat } from "../../store/Currency_Converters/CurrencyConverter.ts";
import InfoWithPopover from "../../store/InfoWithPopover/InfoWithPopover";
import VendorPriceEditor from "./VendorPriceEditor";
import { STATUS_DATA } from "../../utils/statusData";

const statusData = STATUS_DATA;

// Rendered inline by MyAppsFunctions — same model as MyAppsDetail
// (recurring). The back arrow clears selectedApp in the parent instead
// of navigating, so the workspace context above is preserved. navigate
// is kept around because the auth-fail branches inside the fetches still
// punt the user to /login on a 202.
const MyAppsOnetimeDetail = ({ selectedApp, setSelectedApp }) => {
  const navigate = useNavigate();
  const rowData = selectedApp || "";
  const [pastInvoices, setPastInvoices] = useState(null);
  const [pastPayments, setPastPayments] = useState(null);
  const [pastBills, setPastBills] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  //ALL CALCULATION STATES :
  const [currRegionCurrency, setCurrRegionCurrency] = useState(null);
  const [currRegionTaxPer, setCurrRegionTaxPer] = useState("");
  const [quantity, setQuantity] = useState(rowData.noOfLicences);
  const [useEffectExecuted, setUseEffectExecuted] = useState(false);
  const [calculationResults, setCalculationResults] = useState({});
  const [compeleteDataLoader, setCompeleteDataLoader] = useState(true);
  const [pastOrders, setPastOrders] = useState(null);
  const [payConfirmation, setPayConfirmation] = useState(false);
  const [isSomethingChanged, setIsSomethingChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(null);
  const [payId, setPayId] = useState({});
  const REACT_APP_SERVER_URL = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    if (rowData) {
      const fetchCurrRegionData = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/get_region`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setCurrRegionCurrency(data.region.currency);
            setCurrRegionTaxPer(data.region.tax_percent);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastPayments = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_payments`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: rowData.id }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastPayments(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastBills = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_bills`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: rowData.id }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastBills(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastInvoices = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_invoices`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: rowData.id }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastInvoices(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastOrders = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_orders`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: rowData.id }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastOrders(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchModifyOrders = async () => {
        setUseEffectExecuted(false);
        return fetch(`${process.env.REACT_APP_SERVER_URL}/modify_orders`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ price_id: rowData.price_id }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPricingData(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };

      const fetchAllData = async () => {
        setCompeleteDataLoader(true);
        try {
          await fetchCurrRegionData();
          await fetchPastPayments();
          await fetchPastBills();
          await fetchPastInvoices();
          await fetchPastOrders();
          await fetchModifyOrders();
          setUseEffectExecuted(true);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setCompeleteDataLoader(false);
        }
      };

      fetchAllData();
    }
    // eslint-disable-next-line
  }, [isSomethingChanged]);

  useEffect(() => {
    if (useEffectExecuted) {
      if (quantity && rowData.unit_ids) {
        let ind = findIndexByQuantity(rowData.unit_ids, quantity);
        if (ind !== -1) {
          let tempArr = updateArrayWithTotal(
            rowData.unit_ids.filter((item, index) =>
              pricingData?.pricing.unit_price_type === "Partial"
                ? index <= ind
                : index === ind
            ),
            pricingData?.pricing.unit_price_type,
            ind
          );
          // setExistingUnitPrice(tempArr);
          let totalSum = calculateTotalSum(tempArr);
          allCalculations(totalSum)
            .then((results) => {
              setCalculationResults(results);
            })
            .catch((error) => {
              console.error("Error in calculations:", error);
            });
        } else {
          //if quantity execeeds then last range of unit price get into this
          setQuantity(
            Array.isArray(rowData.unit_ids) && rowData.unit_ids.length
              ? rowData.unit_ids[rowData.unit_ids.length - 1].end_range
              : 0,
          );
        }
      }
    }

    // eslint-disable-next-line
  }, [useEffectExecuted, pricingData]);

  const allCalculations = async (subTotalCalculation) => {
    const currencyConversion = async (fromCurrency, amount, toCurrency) => {
      const numericAmount = parseFloat(amount);
      if (fromCurrency === toCurrency) {
        return {
          convertedAmount: numericAmount.toFixed(2),
          conversionRate: "1.000000",
        };
      }

      const apiUrl = `https://api.frankfurter.app/latest?amount=${numericAmount}&from=${fromCurrency}&to=${toCurrency}`;
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      const convertedAmount = data.rates[toCurrency];
      const conversionRate = convertedAmount / numericAmount;

      return {
        convertedAmount: convertedAmount.toFixed(2),
        conversionRate: conversionRate.toFixed(3),
      };
    };

    const tax_per = currRegionTaxPer;
    let amount = 0,
      default_amount = 0,
      tax = 0,
      default_tax = 0,
      subTotal = 0,
      default_subTotal = 0,
      default_currency_sign = "",
      selected_currency_sign = "",
      conversion_rate = 0;

    if (subTotalCalculation) {
      const numericValue = parseFloat(subTotalCalculation);
      amount = numericValue;
    }

    if (currRegionCurrency === pricingData?.pricing.currency) {
      const numericValue = parseFloat(subTotalCalculation);
      default_amount = numericValue;
    }

    if (currRegionCurrency !== pricingData?.pricing.currency) {
      const currencyResult = await currencyConversion(
        pricingData?.pricing.currency,
        subTotalCalculation,
        currRegionCurrency
      );
      default_amount = currencyResult.convertedAmount;
      conversion_rate = currencyResult.conversionRate;
    }
    //Caclulation of tax in case of india only
    if (currRegionCurrency === "INR") {
      tax = amount * (tax_per / 100);
      default_tax = default_amount * (tax_per / 100);
    }
    //CALCULATION OF subtotal = amount + tax:-
    subTotal = amount + tax;
    default_subTotal = parseFloat(default_amount) + parseFloat(default_tax);

    //Addition currency signs :
    default_currency_sign = currRegionCurrency === "INR" ? `₹` : `$`;
    selected_currency_sign =
      pricingData?.pricing.currency === "INR" ? `₹` : `$`;

    return {
      amount: Number(amount).toFixed(2),
      default_amount: Number(default_amount).toFixed(2),
      tax: Number(tax).toFixed(2),
      default_tax: Number(default_tax).toFixed(2),
      subTotal: Number(subTotal).toFixed(2),
      default_subTotal: Number(default_subTotal).toFixed(2),
      default_currency_sign: default_currency_sign,
      selected_currency_sign: selected_currency_sign,
      conversion_rate: conversion_rate,
    };
  };

  function findIndexByQuantity(array, quantity) {
    return array?.findIndex(
      (obj) => quantity >= obj.start_range && quantity <= obj.end_range
    );
  }
  function updateArrayWithTotal(arr, type, ri) {
    return arr.map((item, i) => {
      if (type === "Partial") {
        if (!item.hasOwnProperty("total")) {
          if (i === ri) {
            return {
              ...item,
              total: (
                (quantity - item.start_range + 1) *
                item.marketplace_price
              ).toFixed(2),
              quantity: quantity - item.start_range + 1,
            };
          } else {
            return {
              ...item,
              total: (
                (item.end_range - item.start_range + 1) *
                item.marketplace_price
              ).toFixed(2),
              quantity: item.end_range - item.start_range + 1,
            };
          }
        } else {
          if (i === ri) {
            item.total = (
              (quantity - item.start_range + 1) *
              item.marketplace_price
            ).toFixed(2);
            item.quantity = quantity - item.start_range + 1;
            return item;
          } else {
            item.total = (
              (item.end_range - item.start_range + 1) *
              item.marketplace_price
            ).toFixed(2);
            item.quantity = item.end_range - item.start_range + 1;
            return item;
          }
        }
      } else {
        if (!item.hasOwnProperty("total")) {
          return {
            ...item,
            total: (quantity * item.marketplace_price).toFixed(2),
            quantity: quantity,
          };
        } else {
          item.total = (quantity * item.marketplace_price).toFixed(2);
          item.quantity = quantity;
          return item;
        }
      }
    });
  }

  function calculateTotalSum(arr) {
    let totalSum = 0;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].hasOwnProperty("total")) {
        totalSum += parseFloat(arr[i].total);
      }
    }
    return totalSum;
  }

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setErrorSnackbarOpen(false);
  };

  const payBill = () => {
    fetch(`${REACT_APP_SERVER_URL}/pay_onetime_bill`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        bill_id: payId.bill_id,
      }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((data) => {
        if (data.status === 200) {
          setPayConfirmation(false);
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data.msg);
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <Dialog
        PaperProps={{
          style: { borderRadius: "20px" },
        }}
        open={payConfirmation}
      >
        <div
          style={{
            width: "500px",
            height: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => {
                setPayConfirmation(false);
              }}
            />
          </div>
          <DialogTitle id="alert-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Sure you want to mark as paid!! (Try to mark only if payment is
              recived)
            </p>
          </DialogTitle>
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setPayConfirmation(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => payBill()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
            >
              Verify
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      {!compeleteDataLoader ? (
        <div className={styles.mainContainer}>
          <div
            className={styles.headingContainer}
            style={{ justifyContent: "start!important" }}
          >
            <div className={styles.headingLeftContainer}>
              <div>
                <IoArrowBackSharp
                  onClick={() => {
                    // Mirror MyAppsDetail (recurring) exactly — clear the
                    // selectedApp in the parent so its workspace context
                    // and subs list re-render. No URL change, no unmount.
                    setSelectedApp([]);
                  }}
                  className={styles.backButton}
                />
              </div>
              <h3>{rowData.marketplaceName}</h3>
              <div
                style={{
                  backgroundColor: statusData[rowData.status] || "#000",
                  color: "white",
                  fontFamily: "Poppins",
                  fontSize: "0.7rem",
                  padding: "2px 6px",
                  borderRadius: "6px",
                  border: statusData[rowData.status] || "#000",
                  marginTop: "0.5rem",
                  marginLeft: "1.5rem",
                  marginBottom: "0.5rem",
                  transition:
                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  display: "inline-block",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {rowData.status}
              </div>
            </div>
          </div>
          <div className={styles.horizontalLine} />
          {/* Vendor-cost editor — same component used in the recurring
              Update Subscription drawer. Pure local mutation, no Stripe. */}
          <VendorPriceEditor subscription={rowData} />
          {/* next invoice, auto-cancellation */}
          {/* SUBCRIPTION CONTAINER */}
          <div className={styles.subscriptionContainer}>
            <div className={styles.tableContainer}>
              <div className={styles.tableLeftContainer}>
                <div className={styles.leftColoumn}>
                  <span className={styles.tableTitle}>Created</span>
                </div>
                <div className={styles.rightColoumn}>
                  <span className={styles.tableDesc}>
                    {" "}
                    {rowData &&
                      rowData.createdAt
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}
                    ,{" "}
                    {rowData &&
                      rowData.createdAt
                        .split("T")[1]
                        .split(":")
                        .slice(0, 2)
                        .join(":")}
                  </span>
                </div>
              </div>
              <div className={styles.tableRightContainer}>
                {rowData &&
                  (rowData.required_feilds?.length ?? 0) !== 0 &&
                  rowData.required_feilds.map((item, i) => (
                    <>
                      <div className={styles.leftColoumn}>
                        <span className={styles.tableTitle}>{item.title}</span>
                      </div>
                      <div className={styles.rightColoumn}>
                        <span className={styles.tableDesc}>{item.value}</span>
                      </div>
                    </>
                  ))}
              </div>
            </div>
          </div>
          {/* UPCOMING INVOICE HEADING */}
          <div
            className={styles.upComingInvoiceHeadingContainer}
            style={{ justifyContent: "center!important" }}
          >
            <h3>Purchase Details</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          {/* UPCOMING INVOICE WHOLE CODE FOR ALL PRODUCTS EXCEPT EASEXPENSE DOMAINS */}

          <div className={styles.table}>
            <div className={styles.row}>
              <div
                className={`${styles.cell} ${styles.title}`}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                Description
              </div>
              <div
                className={`${styles.cell} ${styles.title}`}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                Qty
              </div>
              <div
                className={`${styles.cell} ${styles.title}`}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                Unit Price
              </div>
              <div
                className={`${styles.cell} ${styles.title}`}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                Amount
              </div>
            </div>
            {rowData &&
              rowData?.unit_ids?.map((item) => (
                <div className={styles.row}>
                  <div className={styles.cell}>{rowData.marketplaceName}</div>
                  <div className={styles.cell}>{item.quantity}</div>
                  <div className={styles.cell}>
                    {" "}
                    {rowData.currency === "INR" ? "₹" : "$"}
                    {item.marketplace_price}
                  </div>
                  <div className={styles.cell}>
                    {" "}
                    {rowData.currency === "INR" ? "₹" : "$"}
                    {item.quantity * item.marketplace_price}
                  </div>
                </div>
              ))}

            {rowData && calculationResults && (
              <>
                {rowData.coupon && (
                  <div className={styles.row}>
                    <div className={styles.cell}></div>
                    <div className={styles.cell}></div>
                    <div
                      className={styles.cell}
                      style={{ color: "#393939", fontWeight: "550" }}
                    >
                      Applied Coupon ({rowData.coupon.percent_off}% Off)
                    </div>
                    <div
                      className={styles.cell}
                      style={{ color: "#393939", fontWeight: "550" }}
                    >
                      {"-"}
                      {calculationResults.selected_currency_sign}
                      {(
                        (calculationResults.amount *
                          rowData.coupon.percent_off) /
                        100
                      ).toFixed(2)}
                    </div>
                  </div>
                )}
                <div className={styles.row}>
                  <div className={styles.cell}></div>
                  <div className={styles.cell}></div>
                  <div
                    className={styles.cell}
                    style={{ color: "#393939", fontWeight: "550" }}
                  >
                    Total Excluding Tax
                  </div>
                  <div
                    className={styles.cell}
                    style={{ color: "#393939", fontWeight: "550" }}
                  >
                    {calculationResults.selected_currency_sign}
                    {!rowData.coupon && calculationResults.amount}
                    {rowData.coupon &&
                      (
                        calculationResults.amount -
                        (calculationResults.amount *
                          rowData.coupon.percent_off) /
                          100
                      ).toFixed(2)}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.cell}></div>
                  <div className={styles.cell}></div>
                  <div
                    className={styles.cell}
                    style={{ color: "#393939", fontWeight: "550" }}
                  >
                    Total Including Tax
                  </div>
                  <div
                    className={styles.cell}
                    style={{ color: "#393939", fontWeight: "550" }}
                  >
                    {calculationResults.selected_currency_sign}
                    {!rowData.coupon &&
                      parseFloat(calculationResults.subTotal).toFixed(2)}
                    {rowData.coupon &&
                      (
                        parseFloat(calculationResults.subTotal).toFixed(2) -
                        (parseFloat(calculationResults.subTotal).toFixed(2) *
                          rowData.coupon.percent_off) /
                          100
                      ).toFixed(2)}
                  </div>
                </div>
                {calculationResults.conversion_rate !== 0 && (
                  <div className={styles.row}>
                    <div className={styles.cell}></div>
                    <div className={styles.cell}></div>
                    <div
                      className={styles.cell}
                      style={{ color: "#393939", fontWeight: "550" }}
                    >
                      Total In Default currency
                    </div>
                    <div
                      className={styles.cell}
                      style={{ color: "#393939", fontWeight: "550" }}
                    >
                      {calculationResults.default_currency_sign}
                      {parseFloat(calculationResults.default_subTotal)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ONLY ORDER HEADING */}
          <div className={styles.priceHeadingContainer}>
            <h3>Orders</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Name
                </div>

                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Quantity
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Total Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Type
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastOrders &&
                pastOrders.map((item) => (
                  <>
                    <div className={styles.row}>
                      <div className={styles.cell}>Order_{item.id}</div>
                      <div className={styles.cell}>{item.marketplaceName}</div>
                      <div className={styles.cell}>{item.status}</div>
                      <div className={styles.cell}>
                        {item.type === "New" ? item.quantity : item.diff}
                      </div>
                      <div className={styles.cell}>{item.subtotal_default}</div>
                      <div className={styles.cell}>{item.type}</div>
                      <div className={styles.cell}>
                        {convertDateFromTFormat(item.createdAt)}
                      </div>
                    </div>
                  </>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Bills</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Type
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Last Payment
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Action
                </div>
              </div>
              {pastBills &&
                pastBills?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Bill_{item.id}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.subtotal_default}
                    </div>
                    <div className={styles.cell}>{item.type}</div>
                    <div className={styles.cell}>{item.status}</div>
                    <div className={styles.cell}>{item.latestPayment}</div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                    <div className={styles.cell}>
                      {item.status !== "paid" && (
                        <>
                          <button
                            onClick={() => {
                              setPayId({
                                bill_id: item.id,
                              });
                              setPayConfirmation(true);
                            }}
                          >
                            Pay
                          </button>
                        </>
                      )}
                      {item.status === "paid" && "-"}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Payments</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Payment Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Link
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastPayments &&
                pastPayments?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Payment_{item.id}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.bill_id ? `Bill_${item.bill_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.amount}
                    </div>
                    <div className={styles.cell}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                    <div className={styles.cell}>
                      {item.status === "failed" ? (
                        <div style={{ display: "flex", gap: 2 }}>
                          {item.status}
                          <InfoWithPopover reason={item.reason} />
                        </div>
                      ) : (
                        item.status
                      )}
                    </div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Invoices</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Invoice Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Invoice Number
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Payment Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Link
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastInvoices &&
                pastInvoices?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Invoice_{item.id}</div>
                    <div className={styles.cell}>{item.invoice_no}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.bill_id ? `Bill_${item.bill_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.payment_id ? `Payment_${item.payment_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.subtotal}
                    </div>
                    <div className={styles.cell}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={30} />
        </div>
      )}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
      >
        <Alert
          onClose={handleErrorSnackbarClose}
          variant="filled"
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyAppsOnetimeDetail;
